import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { detectGeoLanguage } from "../i18n/geoLocale";

const EXPLICIT_FLAG_KEY = "i18nextLngExplicit";
const SESSION_CHECKED_KEY = "mawered_geo_lang_checked";

/**
 * Applies a geo-IP-based language default on the public landing page only.
 *
 * Scope: anonymous visitors landing on mawered.com. Never runs for
 * authenticated users — their language comes from their account preference
 * (see LanguageSelector / useCompanyInfo) and must not be overridden by
 * where they happen to be connecting from.
 *
 * Never overrides a language the visitor picked themselves via the language
 * selector (EXPLICIT_FLAG_KEY) — geo-detection only fills in a better guess
 * than navigator language when nothing was explicitly chosen yet.
 */
export function useGeoLanguage() {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(EXPLICIT_FLAG_KEY) === "1") return;
    if (sessionStorage.getItem(SESSION_CHECKED_KEY) === "1") return;

    let cancelled = false;
    (async () => {
      const geoLang = await detectGeoLanguage();
      if (cancelled) return;
      sessionStorage.setItem(SESSION_CHECKED_KEY, "1");
      if (!geoLang) return;

      const current = (i18n.resolvedLanguage || i18n.language || "").split("-")[0];
      if (geoLang !== current) {
        i18n.changeLanguage(geoLang);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n]);
}
